/**
 * Latency measurement for multi-location streaming.
 *
 * KEY CONCEPT — Service region vs. stream locations:
 *
 *   A stream group lives in one SERVICE REGION (e.g. eu-central-1). That's where
 *   API calls like StartStreamSession are routed to — it's the control plane.
 *
 *   That same stream group can have GPU instances in multiple LOCATIONS
 *   (e.g. eu-west-1, us-east-1). These are the data plane — where the actual
 *   video encoding and WebRTC streaming happen.
 *
 *   The `Locations` parameter in StartStreamSession is an ORDERED PREFERENCE LIST.
 *   GameLift tries the first region, and falls back to subsequent ones if capacity
 *   is unavailable. The actual WebRTC connection goes peer-to-peer to the chosen
 *   location's GPU — the service region doesn't affect stream latency.
 *
 * This module pings a lightweight endpoint in each configured region to measure
 * round-trip time, then returns regions sorted closest-first. The sorted list
 * is passed as the `locations` parameter when starting a session.
 */

/**
 * Ping a single region by hitting DynamoDB's /ping endpoint.
 * This endpoint supports CORS and returns 200 for GET requests,
 * so we get a clean round trip with no console errors.
 */
async function pingRegion(region: string): Promise<{ region: string; latency: number }> {
  const url = `https://dynamodb.${region}.amazonaws.com/ping`;
  const attempts = 5;
  let minMs = Infinity;

  for (let i = 0; i < attempts; i++) {
    const start = performance.now();
    try {
      await fetch(url, { cache: "no-store" });
    } catch {
      // Network error — still record the RTT
    }
    const elapsed = performance.now() - start;
    if (elapsed < minMs) minMs = elapsed;
  }

  return { region, latency: minMs };
}

export interface LatencyResult {
  region: string;
  latency: number;
}

/**
 * Measure latency to the given stream locations and return them sorted closest-first.
 * Runs all pings in parallel for speed.
 */
export async function measureLatencies(streamLocations: string[]): Promise<LatencyResult[]> {
  const results = await Promise.all(streamLocations.map(pingRegion));
  results.sort((a, b) => a.latency - b.latency);

  console.log(
    "Region latencies (ms):",
    results.map((r) => `${r.region}: ${r.latency.toFixed(0)}ms`).join(", ")
  );

  return results;
}
